import isNil from 'lodash/isNil'

import { TransactionTypeFilter } from '@features/bankTransactions/enums/transactionTypeFilter'
import { BookkeepingItemType } from '@features/reconciliation/enums/bookkeepingItemType'
import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { getItemAmountInAccountCurrency } from '@features/reconciliation/utils/getItemAmountInAccountCurrency'

import { Side } from '../../../../../enums/side'
import { SortDirection } from '../../../../../enums/sortDirection'
import { BookkeepingItemSortProperty } from '../elements/BankTransactionDrawer/enums/bookkeepingItemSortProperty'
import { getAmountBySide } from './getAmountBySide'
import { parseFormattedNumber } from './parseFormattedNumber'

export interface FilterBookkeepingItemsParams {
  items: BookkeepingItem[]
  accountCurrency: string | undefined
  searchQuery?: string
  sortProperty: BookkeepingItemSortProperty
  sortDirection: SortDirection
  types?: BookkeepingItemType[]
  transactionSideFilter?: TransactionTypeFilter
  minAmount?: number
  maxAmount?: number
}

export const filterBookkeepingItems = ({
  items,
  accountCurrency,
  searchQuery,
  sortProperty,
  sortDirection,
  types,
  transactionSideFilter,
  minAmount,
  maxAmount,
}: FilterBookkeepingItemsParams): BookkeepingItem[] => {
  let filteredItems = [...items]

  // search
  if (searchQuery) {
    const query = searchQuery.toLowerCase()
    const parsedNumber = parseFormattedNumber(searchQuery)
    const isNumericSearch = !isNaN(parsedNumber)

    filteredItems = filteredItems.filter((item) => {
      const matchesDescription = item.description.toLowerCase().includes(query)
      const matchesAmount = isNumericSearch && Math.abs(item.amount).toString().startsWith(parsedNumber.toString())

      return matchesDescription || matchesAmount
    })
  }

  // type
  if (types && types.length > 0) {
    filteredItems = filteredItems.filter((item) => types.includes(item.type))
  }

  // transaction side
  if (transactionSideFilter) {
    const targetSide = transactionSideFilter === TransactionTypeFilter.Debit ? Side.Debit : Side.Credit
    filteredItems = filteredItems.filter((item) => item.side === targetSide)
  }

  // amount range
  if (!isNil(minAmount)) {
    filteredItems = filteredItems.filter(
      (item) => Math.abs(getItemAmountInAccountCurrency(item, accountCurrency)) >= minAmount,
    )
  }
  if (!isNil(maxAmount)) {
    filteredItems = filteredItems.filter(
      (item) => Math.abs(getItemAmountInAccountCurrency(item, accountCurrency)) <= maxAmount,
    )
  }

  // sort
  filteredItems.sort((a, b) => {
    const aValue = sortProperty === BookkeepingItemSortProperty.Amount ? getAmountBySide(a) : new Date(a.date).getTime()
    const bValue = sortProperty === BookkeepingItemSortProperty.Amount ? getAmountBySide(b) : new Date(b.date).getTime()

    let comparison = 0
    if (aValue < bValue) {
      comparison = -1
    } else if (aValue > bValue) {
      comparison = 1
    }

    return sortDirection === SortDirection.Asc ? comparison : -comparison
  })

  return filteredItems
}
